import {
  AnimatedPage,
  Flex,
  FlexAnchor,
  Header,
  HeaderTitle,
  Icon,
  LinkAnchor,
  MainSection,
  PrimaryButton,
  SideSection,
  StickySidebar,
  WideContainer,
} from '@wrisk/ui-components'
import { isNil } from 'lodash-es'
import React, { FunctionComponent, useState } from 'react'
import { Link, useNavigate } from 'react-router'

import {
  coverDifferencesToDisclosureChange,
  CoverOption,
  getCurrentQuote,
  isAddOn,
} from '../../../../../domain'
import { useOptionalCovers } from '../../../../../hooks/covers'
import { useErrorHandlingAsyncCallback } from '../../../../../hooks/errors'
import {
  tDefaults,
  TKey,
  useWriskTranslation,
} from '../../../../../infrastructure/internationalisation'
import { useUpButton } from '../../../../../infrastructure/routing'
import { usePrincipal } from '../../../../authentication'
import { AddOnSelector } from '../../../../organisms/covers'
import { Footer } from '../../../../templates'
import { useProduct } from '../../../productContext'
import { useProposal } from '../../ProposalProvider'
import { useCheckout } from '../CheckoutProvider'
import { ProposalAddOnsSidebar } from './ProposalAddOnsSidebar'

const tKey = TKey('pages.proposal-add-ons')

export const ProposalAddOnsPage: FunctionComponent = () => {
  const { t } = useWriskTranslation()

  const navigate = useNavigate()

  const { previous, next } = useCheckout()
  const { apiClient } = usePrincipal()
  const { proposal, setProposal } = useProposal()
  const { product } = useProduct()

  const upButton = useUpButton(previous)

  const quote = getCurrentQuote(proposal)

  const originalCoverOptions = useOptionalCovers(quote, product).filter(isAddOn(product))

  const [updatedCoverOptions, setUpdatedCoverOptions] = useState<CoverOption[]>(
    originalCoverOptions.map((coverOption) => ({
      ...coverOption,
      active: coverOption.active || undefined,
    })),
  )

  const originalCovers = originalCoverOptions.filter((it) => it.available)

  const updatedCovers = updatedCoverOptions.filter((it) => it.available)

  const disabled = updatedCovers.some((it) => isNil(it.active))

  const { loading, execute: onContinue } = useErrorHandlingAsyncCallback(async () => {
    const changes = [...coverDifferencesToDisclosureChange(updatedCovers, originalCovers)]

    if (changes.length) {
      setProposal(await apiClient.updateProposal(proposal.proposalCode, { changes }))
    }

    navigate(next.url)
  })

  return (
    <AnimatedPage pageId='add-ons-page' footer={<Footer />}>
      <WideContainer>
        <Header>
          <Flex width={1}>
            <FlexAnchor
              fontFamily='buttons'
              mb={10}
              as={Link}
              to={upButton?.to ?? '/'}
              data-testid='link-back'
            >
              <Icon size='small' pl={2} icon='chevronRight' rotate={180} />
              <LinkAnchor variant='standalone' as='span' typoSize='sm'>
                {upButton?.text || 'Back'}
              </LinkAnchor>
            </FlexAnchor>
          </Flex>
          <HeaderTitle>{t(tKey('header'))}</HeaderTitle>
        </Header>
      </WideContainer>
      <WideContainer variant='sidebar'>
        <StickySidebar>
          <SideSection>
            <ProposalAddOnsSidebar
              optionalCovers={updatedCovers}
              onContinue={onContinue}
              disabled={disabled}
              loading={loading}
            />
          </SideSection>
        </StickySidebar>
        <MainSection>
          <AddOnSelector
            coverOptions={updatedCoverOptions}
            quote={quote}
            onChange={setUpdatedCoverOptions}
          />
          <ProposalAddOnsSidebar
            displayOn='mobile'
            optionalCovers={updatedCovers}
            disabled={disabled}
            loading={loading}
            onContinue={onContinue}
          />

          <Flex displayOn='desktop' width={1} mt={5} data-testid='button-continue'>
            <PrimaryButton
              layout='fixed'
              loading={loading}
              disabled={disabled}
              onClick={onContinue}
              data-testid='button-continue'
            >
              {t([tKey('actions.continue'), tDefaults('actions.continue')])}
            </PrimaryButton>
          </Flex>
        </MainSection>
      </WideContainer>
    </AnimatedPage>
  )
}
